/** @format */

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import StripeForm from "./stripe-form/StripForm";
import axios from "axios";
import { useRef } from "react";
import { toast } from "react-toastify";

const StripeCardPayment = ({
  keys,
  stripeId,
  amount,
  currency,
  country,
  reference,
  customerEmail,
  query,
  customerName,
  onPaymentSuccess,
  description,
  setUser,
  user,
  merchantId,
  merchantName,
  onlyAllowedPayment,
  productList,
  productsAndAmount,
  isGiftAid,
  merchantDetails,
  redirectWhenCancelledUrl,
  redirectWhenSuccessfulUrl
}) => {
  const isGhana = country?.alpha2Code === "GH";
  const [stripePromise, setStripePromise] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const hasFetchedSession = useRef(false);
  const [loading, setLoading] = useState(isGhana);

  // Load Stripe keys
  useEffect(() => {
    if (keys) {
      const stripe = loadStripe(
        keys,
        stripeId
          ? {
              stripeAccount: stripeId,
            }
          : undefined
      );
      setStripePromise(stripe);
    }
  }, [keys, stripeId]);

  useEffect(() => {
    const fetchSessionId = async () => {
      try {
        let firstName = ".";
        let lastName = ".";
        if (customerName && typeof customerName === "string") {
          const parts = customerName.trim().split(" ");
          if (parts.length > 0 && parts[0]) firstName = parts[0];
          if (parts.length > 1 && parts[1]) lastName = parts.slice(1).join(" ");
        }

        let productObj = {};
        let commentsObj = {};

        try {
          // Prefer explicit selected products passed in via productsAndAmount
          // productsAndAmount shape comes from HandleAmount: a map of productId -> { amount, comment, ... }
          const source = productsAndAmount && typeof productsAndAmount === "object" && !Array.isArray(productsAndAmount)
            ? productsAndAmount
            : (() => {
                let descObj = description;
                if (typeof description === "string") {
                  try { descObj = JSON.parse(description); } catch (_) {}
                }
                return descObj;
              })();

          if (source && typeof source === "object" && !Array.isArray(source)) {
            Object.entries(source).forEach(([productId, value]) => {
              const amt = Number(value?.amount ?? 0);
              if (amt && amt !== 0) {
                productObj[productId] = amt;
                commentsObj[productId] = value?.comment || "";
              }
            });
          }
        } catch (e) {
          // ignore
        }

        // Identify fixed-amount (default) products among the selected ones
        const fixedAmountFlags = {};
        const fixedAmountProductIds = [];
        if (Array.isArray(productList)) {
          Object.keys(productObj).forEach((pid) => {
            const prod = productList.find((p) => String(p?.product_id) === String(pid));
            if (prod && typeof prod.amount !== "undefined" && Number(prod.amount) > 0) {
              fixedAmountFlags[pid] = true;
              fixedAmountProductIds.push(pid);
            }
          });
        }

        const response = await axios.post(
          "https://api.xcelapp.com/transactions-service/mastercard/initiate/checkout",
          {
            amount,
            clientTransactionId: reference,
            currency: "GHS",
            firstName,
            lastName,
            productId: JSON.stringify(productObj),
            description: `Payment for ${reference}`,
            comments: JSON.stringify(commentsObj),
            email: customerEmail,
            phoneNumber: "none",
            merchantId,
            returnUrl: `${window.location.origin}?ref=${reference}&amt=${amount}&successPage=mastercard&redirectWhenSuccessfulUrl=${redirectWhenSuccessfulUrl}&redirectWhenCancelledUrl=${redirectWhenCancelledUrl}`,
            cancelUrl: `${window.location.origin}?ref=${reference}&amt=${amount}&successPage=mastercard&redirectWhenSuccessfulUrl=${redirectWhenSuccessfulUrl}&redirectWhenCancelledUrl=${redirectWhenCancelledUrl}`,
            notificationUrl: `${window.location.origin}?ref=${reference}&amt=${amount}&successPage=mastercard&redirectWhenSuccessfulUrl=${redirectWhenSuccessfulUrl}&redirectWhenCancelledUrl=${redirectWhenCancelledUrl}`,
          }
        );

        const sessionId = response.data.sessionId;
        
        if (sessionId) {
          setSessionId(sessionId);
          window.Checkout.configure({
            session: {
              id: sessionId,
            },
          });
        } else {
          toast.error("Session ID not returned");
        }
      } catch (error) {
        toast.error("Failed to fetch session ID");
        console.error("Failed to fetch session ID: ", error);
      }
    };

    if (isGhana && !hasFetchedSession.current) {
      hasFetchedSession.current = true;
      fetchSessionId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGhana]);

  // Define global callbacks
  useEffect(() => {
    if (isGhana) {
      window.errorCallback = (error) => {
        toast.error("Payment Error");
        console.error("Payment Error: ", error);
      };

      window.cancelCallback = () => {
        toast.error("Payment cancelled");
      };
    }
  }, [isGhana]);

  useEffect(() => {
    if (isGhana && sessionId && window.Checkout) {
      setLoading(false);
      window.Checkout.showPaymentPage();
    }
  }, [isGhana, sessionId]);

  if (isGhana) {
    return (
      <div className="w-full md:w-[300px] lg:w-[500px] lg:h-[400px] my-8 flex justify-center">
        {loading ? (
          <div className="text-center py-4">
            <svg
              className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
            <p className="my-4">Loading secure payment gateway...</p>
          </div>
        ) : null}
      </div>
    );
  }

  // Non-Ghana users: use Stripe Elements
  return (
    stripePromise && (
      <Elements stripe={stripePromise}>
        <StripeForm
          amount={amount}
          currency={currency}
          onlyAllowedPayment={onlyAllowedPayment}
          reference={reference}
          customerEmail={customerEmail}
          customerName={customerName}
          alpha2CountryCode={query.alpha2CountryCode}
          onPaymentSuccess={onPaymentSuccess}
          description={description}
          stripeId={stripeId}
          productList={productList}
          productsAndAmount={productsAndAmount}
          isGiftAid={isGiftAid}
          setNameGlobal={(e) =>
            setUser({
              ...user,
              first_name: e,
              last_name: "",
            })
          }
          merchantId={query.merchantId}
          merchantDetails={merchantDetails}
        />
      </Elements>
    )
  );
};

export default StripeCardPayment;
